import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Eye, EyeOff, AlertCircle, CheckCircle2, ArrowRight } from 'lucide-react';
import AuthClient from '../data/api/AuthClient';


type OrgType = 'Corporate' | 'Startup' | 'Enterprise' | 'SME' | 'Government' | 'Non-Profit';

interface FormData {
  email_address: string;
  company_name: string;
  organization_type: OrgType | '';
  headquarters_location: string;
  website_url: string;
  contact_number: string;
  contact_email_address: string;
  admin_password: string;
}

const ORG_TYPES: OrgType[] = ['Corporate', 'Startup', 'Enterprise', 'SME', 'Government', 'Non-Profit'];

const STEPS = [
  { id: 1, label: 'Email Verification', sub: 'Check your business email' },
  { id: 2, label: 'Company Identity', sub: 'Core details & presence' },
  { id: 3, label: 'Contact Details', sub: 'Primary contact info' },
  { id: 4, label: 'Account Security', sub: 'Credentials & access' },
];

const stepVariants = {
  enter: (dir: number) => ({ x: dir > 0 ? 80 : -80, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir: number) => ({ x: dir > 0 ? -80 : 80, opacity: 0 }),
};

const RegisterComponent: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [form, setForm] = useState<FormData>({
    email_address: '',
    company_name: '',
    organization_type: '',
    headquarters_location: '',
    website_url: '',
    contact_number: '',
    contact_email_address: '',
    admin_password: '',
  });

  const update = (field: keyof FormData, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateStep = () => {
    if (step === 1) {
      if (!form.email_address.trim() || !form.email_address.includes('@')) return 'A valid business email is required.';
    }
    if (step === 2) {
      if (!form.company_name.trim()) return 'Company name is required.';
      if (!form.organization_type) return 'Please select an organization type.';
      if (!form.headquarters_location.trim()) return 'Headquarters location is required.';
      if (!form.website_url.trim()) return 'Website URL is required.';
    }
    if (step === 3) {
      if (!form.contact_number.trim()) return 'Contact number is required.';
    }
    if (step === 4) {
      if (form.admin_password.length < 8) return 'Password must be at least 8 characters.';
    }
    return '';
  };

  const handleEmailCheck = async () => {
    const err = validateStep();
    if (err) { setError(err); return; }
    setLoading(true);
    setError('');
    try {
      const res = await AuthClient.checkEmail({
        email_address: form.email_address.trim().toLowerCase(),
        entity: 'INTERVIEWIQ_ADMIN',
      });
      if (res.error && res.error !== 'Email does not exist.') {
        setError(res.error);
        return;
      }
      setDirection(1);
      setStep(2);
    } catch {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const next = () => {
    const err = validateStep();
    if (err) { setError(err); return; }
    setDirection(1);
    setStep(s => s + 1);
    setError('');
  };

  const back = () => {
    setDirection(-1);
    setStep(s => s - 1);
    setError('');
  };

  const handleSubmit = async () => {
    const err = validateStep();
    if (err) { setError(err); return; }
    setLoading(true);
    setError('');
    try {
      const res = await AuthClient.register({
        company_name: form.company_name.trim(),
        organization_type: form.organization_type,
        headquarters_location: form.headquarters_location.trim(),
        website_url: form.website_url.trim(),
        email_address: form.email_address.trim().toLowerCase(),
        contact_number: form.contact_number.trim(),
        admin_password: form.admin_password,
        contact_email_address: form.contact_email_address.trim() || undefined,
      });
      if (res.error) {
        setError(res.error);
        return;
      }
      setSuccess(true);
    } catch {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <motion.div className="auth-success-card login-form-wrapper" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
        <div className="success-icon-ring"><CheckCircle2 size={48} color="#0d6e5b" /></div>
        <h2>Registration Complete!</h2>
        <p>Your Joz HRM Corporate account has been created. You can now log in to your portal.</p>
        <button className="btn-primary" onClick={() => navigate('/login')}>Go to Login</button>
      </motion.div>
    );
  }

  const totalSteps = STEPS.length;

  return (
    <div className="login-form-wrapper">
      <div className="brand-header" style={{ justifyContent: 'center' }}>
        <img src="/logo_login_name.png" alt="InterviewIQ Logo" className="brand-logo-img" />
      </div>

      {/* <div className="portal-label">CORPORATE PORTAL</div> */}
      <h2 className="welcome-title">Create Your Account</h2>
      <p className="welcome-subtitle">Step {step} of {totalSteps} — {STEPS[step - 1].label}</p>

      <AnimatePresence custom={direction} mode="wait">
        <motion.div
          key={step}
          custom={direction}
          variants={stepVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.35, ease: 'easeInOut' }}
        >
          {step === 1 && <Step1 form={form} update={update} />}
          {step === 2 && <Step2 form={form} update={update} />}
          {step === 3 && <Step3 form={form} update={update} />}
          {step === 4 && <Step4 form={form} update={update} showPassword={showPassword} setShowPassword={setShowPassword} />}
        </motion.div>
      </AnimatePresence>

      {error && (
        <motion.div className="auth-error-banner" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
          <AlertCircle size={16} /> {error}
        </motion.div>
      )}

      <div className="register-nav" style={{ marginTop: '20px' }}>
        {step > 1 && (
          <button className="auth-btn-ghost" onClick={back}>
            Back
          </button>
        )}
        {step === 1 ? (
          <button className="btn-primary" onClick={handleEmailCheck} disabled={loading || !form.email_address} style={{ width: 'auto', padding: '12px 32px' }}>
            {loading ? <span className="auth-spinner" /> : <><Mail size={18} /> Verify Email</>}
          </button>
        ) : step < totalSteps ? (
          <button className="btn-primary" onClick={next} style={{ width: 'auto', padding: '12px 32px' }}>
            Next Step <ArrowRight size={18} />
          </button>
        ) : (
          <button className="btn-primary" onClick={handleSubmit} disabled={loading} style={{ width: 'auto', padding: '12px 32px' }}>
            {loading ? <span className="auth-spinner" /> : <><CheckCircle2 size={18} /> Complete Onboarding</>}
          </button>
        )}
      </div>
    </div>
  );
};

const Step1: React.FC<{ form: FormData; update: (f: keyof FormData, v: string) => void }> = ({ form, update }) => (
  <div>
    <div className="form-group border-input-group">
      <label className="field-label">Email Address</label>
      <input id="email_address" className="form-input-line" type="email" value={form.email_address} onChange={e => update('email_address', e.target.value)} />
    </div>
  </div>
);

const Step2: React.FC<{ form: FormData; update: (f: keyof FormData, v: string) => void }> = ({ form, update }) => (
  <div>
    <div className="form-group border-input-group">
      <label className="field-label">Company Name</label>
      <input id="company_name" className="form-input-line" value={form.company_name} onChange={e => update('company_name', e.target.value)} />
    </div>

    <div className="form-group border-input-group">
      <label className="field-label">Organization Type</label>
      <select id="organization_type" className="form-input-line" value={form.organization_type} onChange={e => update('organization_type', e.target.value as OrgType)}>
        <option value="">Select type</option>
        {ORG_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
      </select>
    </div>

    <div className="form-group border-input-group">
      <label className="field-label">Headquarters Location</label>
      <input id="headquarters_location" className="form-input-line" value={form.headquarters_location} onChange={e => update('headquarters_location', e.target.value)} />
    </div>

    <div className="form-group border-input-group">
      <label className="field-label">Website URL</label>
      <input id="website_url" className="form-input-line" value={form.website_url} onChange={e => update('website_url', e.target.value)} />
    </div>
  </div>
);

const Step3: React.FC<{ form: FormData; update: (f: keyof FormData, v: string) => void }> = ({ form, update }) => (
  <div>
    <div className="form-group border-input-group">
      <label className="field-label">Business Email</label>
      <input id="email_address_display" className="form-input-line" type="email" value={form.email_address} disabled />
    </div>

    <div className="form-group border-input-group">
      <label className="field-label">Contact Number</label>
      <input id="contact_number" className="form-input-line" value={form.contact_number} onChange={e => update('contact_number', e.target.value)} />
    </div>

    <div className="form-group border-input-group">
      <label className="field-label">Alternate Contact Email (optional)</label>
      <input id="contact_email_address" className="form-input-line" type="email" value={form.contact_email_address} onChange={e => update('contact_email_address', e.target.value)} />
    </div>
  </div>
);

const Step4: React.FC<{ form: FormData; update: (f: keyof FormData, v: string) => void; showPassword: boolean; setShowPassword: (v: boolean) => void }> = ({ form, update, showPassword, setShowPassword }) => (
  <div>
    <div className="form-group border-input-group">
      <label className="field-label">Password</label>
      <div className="password-input-wrap">
        <input id="admin_password" className="form-input-line password-input" type={showPassword ? 'text' : 'password'} value={form.admin_password} onChange={e => update('admin_password', e.target.value)} />
        <button type="button" className="password-toggle-btn" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
    </div>

    <div className="terms-notice">
      <AlertCircle size={15} color="#0d6e5b" />
      <p>By completing registration, you agree to Joz HRM's <span className="auth-link">Terms of Service</span> and <span className="auth-link">data privacy policies</span> for enterprise clients.</p>
    </div>
  </div>
);

export default RegisterComponent;
